import React, { useState, useEffect, useRef } from "react";
import "./KPIDashboardScreen.css";
import "./CrewPilotSharedForm.css";
import { CrewPilotFormShell } from "./CrewPilotFormShell";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { HubPrimaryAction } from "./shared/HubPrimaryAction";
import {
  RecBar,
  useKeepFocusedFieldVisible,
  useKeyboardOpen,
} from "./materials/MaterialRecordParts";
import "./project/projectForm.css";
import "./clientForm.css";
import { ClientWorkspaceSection } from "./ClientWorkspaceSection";
import { CPInput } from "./CPInput";
import { CPTextarea } from "./CPTextarea";
import { CPDropdown } from "./CPDropdown";
import { ACTION_BTN_SM } from "./estimate/estimateStyles";
import { supabase } from "../supabase";
import { insertScopedClient, loadScopedClients, updateScopedClient } from "../helpers/operationalQueryAdapters";
import {
  archiveClient,
  deleteClientPermanently,
  getActiveClients,
  getArchivedClients,
  getClientDeleteEligibility,
  isClientArchived,
  isClientPermanentlyDeletable,
  restoreClient,
} from "../helpers/clientVisibility";
import {
  notifyClientArchived,
  notifyClientRestored,
} from "../helpers/operationalLifecycle";
import {
  getClientBlastRadius,
  isSupportedBlastRadiusSchema,
} from "../helpers/clientBlastRadius";
import { ClientBlastRadiusScreen } from "./ClientBlastRadiusScreen";
import { ClientIntelligenceScreen } from "./ClientIntelligenceScreen";
import { BiohazardMark } from "./icons/BiohazardIcon";
import { ClientsDesktopHub } from "./ClientsDesktopHub";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import "./clientHub.css";
import "./ClientBlastRadiusScreen.css";
import { blockDemoSave, readGuestSession, shouldSkipSupabase } from "../helpers/guestMode";
import { useHardwareBackHandler } from "../navigation/HardwareBackContext";
import { usePermissions } from "../hooks/usePermissions";
import { useDashboardCarouselSwipe } from "../hooks/useDashboardCarouselSwipe";
import {
  HUB_STATUS_PILL_ACTIVE_GOLD_STYLE,
  HUB_STATUS_PILL_BTN_BASE_STYLE,
} from "./shared/hubFilterPillStyles";
import { PAGE_LAYOUT } from "../layout/pageLayout";

// ── Question definitions for Create Client flow ────────────

// ── Internal screens ───────────────────────────────────────

const SEARCH_INPUT_STYLE = {
  width: "100%",
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px solid var(--nx-border, #e2e5eb)",
  background: "var(--nx-surface, #fff)",
  color: "var(--nx-text, #1a2233)",
  fontSize: "16px",
  boxSizing: "border-box",
};

const CLIENT_HUB_STATUS_FILTERS = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
];

const STATUS_PILL_BTN_BASE_STYLE = HUB_STATUS_PILL_BTN_BASE_STYLE;

const CF_ICONS = {
  doc: <path d="M7 3.5h7l4 4V19a1.5 1.5 0 0 1-1.5 1.5h-9.5A1.5 1.5 0 0 1 5.5 19V5A1.5 1.5 0 0 1 7 3.5ZM13.5 3.5V8H18M8.5 12h7M8.5 15.5h7" />,
  pin: (
    <>
      <path d="M12 21s6.5-5.6 6.5-11a6.5 6.5 0 0 0-13 0c0 5.4 6.5 11 6.5 11Z" />
      <circle cx="12" cy="10" r="2.3" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.5 19c.4-3 2.7-4.8 5.5-4.8s5.1 1.8 5.5 4.8M16 6a2.7 2.7 0 0 1 0 5.3M17.5 14.5c1.9.5 3 2 3.3 4.5" />
    </>
  ),
  notes: <path d="M6 4h12a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1ZM8.5 9h7M8.5 12.5h7M8.5 16h4" />,
};

function CfIcon({ name }) {
  return (
    <svg
      className="pf-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {CF_ICONS[name]}
    </svg>
  );
}

function ClientMobileForm({
  title,
  subtitle,
  onCancel,
  onSubmit,
  saveDisabled,
  showArchived,
  clientName,
  setClientName,
  contactName,
  setContactName,
  phone,
  setPhone,
  secondaryPhone,
  setSecondaryPhone,
  email,
  setEmail,
  address,
  setAddress,
  city,
  setCity,
  province,
  setProvince,
  postalCode,
  setPostalCode,
  country,
  setCountry,
  clientStatus,
  setClientStatus,
  statusDisabled,
  notes,
  setNotes,
  extras,
}) {
  useKeyboardOpen(true);
  useKeepFocusedFieldVisible(true);

  return (
    <div
      className="screen nx-screen rec-page rec-page--edit pf-page cf-page"
      data-testid="client-form"
    >
      <RecBar title={title} leading="back" onBack={onCancel} />
      {subtitle ? <div className="pf-subtitle">{subtitle}</div> : null}

      <div className="rec-body pf-form cp-form-compact">
        {showArchived ? <div style={ARCHIVED_BADGE_STYLE}>Archived Client</div> : null}

        <section className="rec-card pf-card" aria-label="Client information">
          <div className="pf-head">
            <CfIcon name="doc" />
            <span>Client Information</span>
          </div>
          <CPInput
            label="Client Name"
            value={clientName}
            onChange={setClientName}
            placeholder="Enter client name"
            required
          />
          <CPInput
            label="Contact Name"
            value={contactName}
            onChange={setContactName}
            placeholder="Enter contact name"
          />
        </section>

        <section className="rec-card pf-card" aria-label="Contact information">
          <div className="pf-head">
            <CfIcon name="people" />
            <span>Contact Information</span>
          </div>
          <div className="cp-row-grid-2" data-testid="client-phone-row">
            <CPInput
              label="Phone"
              value={phone}
              onChange={setPhone}
              placeholder="Phone number"
              type="tel"
              required
            />
            <CPInput
              label="Secondary Phone"
              value={secondaryPhone}
              onChange={setSecondaryPhone}
              placeholder="Optional"
              type="tel"
            />
          </div>
          <CPInput
            label="Email"
            value={email}
            onChange={setEmail}
            placeholder="Enter email address"
            type="email"
            required
          />
        </section>

        <section className="rec-card pf-card" aria-label="Address">
          <div className="pf-head">
            <CfIcon name="pin" />
            <span>Address</span>
          </div>
          <CPInput
            label="Address"
            value={address}
            onChange={setAddress}
            placeholder="Enter street address"
          />
          <div className="cp-row-grid-2" data-testid="client-city-row">
            <CPInput
              label="City"
              value={city}
              onChange={setCity}
              placeholder="City"
            />
            <CPInput
              label="Province"
              value={province}
              onChange={setProvince}
              placeholder="Province"
            />
          </div>
          <div className="cp-row-grid-2" data-testid="client-postal-row">
            <CPInput
              label="Postal Code"
              value={postalCode}
              onChange={setPostalCode}
              placeholder="Postal code"
            />
            <CPInput
              label="Country"
              value={country}
              onChange={setCountry}
              placeholder="Country"
            />
          </div>
        </section>

        <section className="rec-card pf-card" aria-label="Details">
          <div className="pf-head">
            <CfIcon name="notes" />
            <span>Details</span>
          </div>
          <CPDropdown
            label="Client Status"
            value={clientStatus === "archived" ? "active" : clientStatus}
            onChange={setClientStatus}
            placeholder="Select status"
            options={[
              { value: "active", label: "Active" },
              { value: "inactive", label: "Inactive" },
              { value: "prospect", label: "Prospect" },
            ]}
            disabled={statusDisabled}
          />
          <CPTextarea
            label="Notes"
            value={notes}
            onChange={setNotes}
            placeholder="Enter client notes"
            rows={2}
          />
        </section>

        {extras}
      </div>

      <div className="rec-actions rec-actions--split">
        <button type="button" className="rec-btn rec-btn--ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="rec-btn rec-btn--primary"
          onClick={onSubmit}
          disabled={saveDisabled}
        >
          Save Client
        </button>
      </div>
    </div>
  );
}

const ARCHIVED_BADGE_STYLE = {
  display: "inline-block",
  marginTop: "8px",
  padding: "4px 8px",
  borderRadius: "8px",
  border: "1px solid rgba(201,168,76,0.35)",
  background: "rgba(201,168,76,0.12)",
  color: "rgba(240,230,204,0.92)",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const DELETE_CLIENT_BTN_STYLE = {
  ...ACTION_BTN_SM,
  border: "1px solid rgba(224,107,90,0.55)",
  background: "rgba(224,107,90,0.12)",
  color: "#f0a8a0",
};

const DELETE_CLIENT_CONFIRM_PANEL_STYLE = {
  marginTop: "4px",
  padding: "14px",
  borderRadius: "12px",
  border: "1px solid rgba(224,107,90,0.45)",
  background: "rgba(224,107,90,0.08)",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
};

function clientMatchesSearch(client, search) {
  if (!search) return true;

  return (
    client.client_name?.toLowerCase().includes(search) ||
    client.contact_name?.toLowerCase().includes(search) ||
    client.phone?.toLowerCase().includes(search) ||
    client.email?.toLowerCase().includes(search)
  );
}

function getHubClientsForStatusFilter(clients, statusFilter) {
  if (statusFilter === "archived") {
    return getArchivedClients(clients);
  }

  return getActiveClients(clients);
}

function clientInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return `${first}${last}`.toUpperCase();
}

function ClientHubCard({
  client,
  onSelect,
  onArchive,
  onRestore,
  canManageArchive,
}) {
  const archived = isClientArchived(client);

  return (
    <div className={`ch-card${archived ? " is-archived" : ""}`}>
      <div
        className="ch-card__row"
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: "14px",
        }}
      >
        <span className="ch-card__avatar" aria-hidden="true">
          {clientInitials(client.client_name)}
        </span>
        <button
          className="ch-card__main"
          type="button"
          onClick={() => onSelect(client.client_id)}
          style={{
            flex: "1 1 auto",
            minWidth: 0,
            background: "none",
            border: "none",
            padding: 0,
            margin: 0,
            textAlign: "left",
            color: "inherit",
            cursor: "pointer",
          }}
        >
          <div
            className="ch-card__name"
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "8px",
            }}
          >
            {client.client_name || "Unnamed Client"}
          </div>

          {client.contact_name && (
            <div style={{ opacity: 0.75 }}>Contact: {client.contact_name}</div>
          )}

          {client.phone && (
            <div style={{ opacity: 0.75 }}>Phone: {client.phone}</div>
          )}

          {client.email && (
            <div style={{ opacity: 0.75 }}>Email: {client.email}</div>
          )}

          {archived ? <div style={ARCHIVED_BADGE_STYLE}>Archived</div> : null}
        </button>

        {canManageArchive ? (
          <div className="ch-card__actions" style={{ flexShrink: 0, alignSelf: "flex-end" }}>
            {archived ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRestore(client.client_id);
                }}
                style={ACTION_BTN_SM}
              >
                Restore
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onArchive(client.client_id);
                }}
                style={ACTION_BTN_SM}
              >
                Archive
              </button>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function resolveCreateClientInitialView({
  embedFromEstimate = false,
  initialView = "hub",
}) {
  if (embedFromEstimate) {
    return "form";
  }
  return initialView;
}

export function CreateClientScreen({
  onBack,
  embedFromEstimate = false,
  onClientCreated,
  onCancel,
  initialView = "hub",
  onCarouselNavigate,
  onOpenNavigator,
  onOpenProjectDetail,
  onOpenInvoice,
  onOpenProjectsForClient,
  onOpenTimeLogsForClient,
}) {
  const { actor, can, CAPABILITY } = usePermissions();
  const canManageArchive = can(CAPABILITY.CLIENT_ARCHIVE);
  const isDesktopViewport = useIsDesktopViewport();
  const canManageDelete = can(CAPABILITY.CLIENT_DELETE);
  const canInspectBlastRadius = can(CAPABILITY.CLIENT_BLAST_RADIUS);
  const touchStartX = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [view, setView] = useState(() =>
    resolveCreateClientInitialView({ embedFromEstimate, initialView }),
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [hubStatusFilter, setHubStatusFilter] = useState("active");
  const [mode, setMode] = useState("create");

  const [selectedClientId, setSelectedClientId] = useState("");
  const [deleteEligibility, setDeleteEligibility] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [blastRadiusPayload, setBlastRadiusPayload] = useState(null);
  const [blastRadiusBusy, setBlastRadiusBusy] = useState(false);
  const [blastRadiusError, setBlastRadiusError] = useState("");
  const [showBlastRadius, setShowBlastRadius] = useState(false);

  const [allClients, setAllClients] = useState([]);
  useEffect(() => {
    if (!actor) {
      return;
    }

    loadAllClients();
  }, [actor, canManageArchive]);

  useEffect(() => {
    if (!canManageArchive && hubStatusFilter === "archived") {
      setHubStatusFilter("active");
    }
  }, [canManageArchive, hubStatusFilter]);
  useEffect(() => {
    if (!selectedClientId) return;

    const client = allClients.find((c) => c.client_id === selectedClientId);

    if (!client) return;

    setClientName(client.client_name || "");
    setContactName(client.contact_name || "");

    setPhone(client.phone || "");
    setSecondaryPhone(client.secondary_phone || "");
    setEmail(client.email || "");

    setAddress(client.address || "");
    setCity(client.city || "");
    setProvince(client.province || "");
    setPostalCode(client.postal_code || "");
    setCountry(client.country || "");

    setClientStatus(client.client_status || "active");

    setNotes(client.notes || "");
  }, [selectedClientId, allClients]);

  const [clientName, setClientName] = useState("");
  const [contactName, setContactName] = useState("");

  const [phone, setPhone] = useState("");
  const [secondaryPhone, setSecondaryPhone] = useState("");
  const [email, setEmail] = useState("");

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");

  const [clientStatus, setClientStatus] = useState("active");

  const [notes, setNotes] = useState("");
  const CLIENT_DRAFT_KEY = "createClientDraft";

  useEffect(() => {
    if (embedFromEstimate) return;
    if (view !== "form" || mode !== "create") return;

    const savedDraft = localStorage.getItem(CLIENT_DRAFT_KEY);

    if (!savedDraft) return;

    try {
      console.log("LOADING CLIENT DRAFT", savedDraft);
      const draft = JSON.parse(savedDraft);

      setClientName(draft.clientName || "");
      setContactName(draft.contactName || "");

      setPhone(draft.phone || "");
      setSecondaryPhone(draft.secondaryPhone || "");

      setEmail(draft.email || "");

      setAddress(draft.address || "");
      setCity(draft.city || "");
      setProvince(draft.province || "");
      setPostalCode(draft.postalCode || "");
      setCountry(draft.country || "");

      setClientStatus(draft.clientStatus || "active");

      setNotes(draft.notes || "");
    } catch (error) {
      console.error("CLIENT DRAFT LOAD ERROR:", error);
    }
  }, [embedFromEstimate, view, mode]);

  useEffect(() => {
    if (embedFromEstimate) return;
    if (view !== "form" || mode !== "create") return;

    if (!clientName && !contactName && !phone && !email && !notes) {
      return;
    }

    localStorage.setItem(
      CLIENT_DRAFT_KEY,
      JSON.stringify({
        clientName,
        contactName,

        phone,
        secondaryPhone,

        email,

        address,
        city,
        province,
        postalCode,
        country,

        clientStatus,

        notes,
      }),
    );
  }, [
    embedFromEstimate,
    view,
    mode,
    clientName,
    contactName,

    phone,
    secondaryPhone,

    email,

    address,
    city,
    province,
    postalCode,
    country,

    clientStatus,

    notes,
  ]);

  useEffect(() => {
    if (mode === "edit") {
      localStorage.removeItem(CLIENT_DRAFT_KEY);
    }
  }, [mode]);

  // Called by ConversationalIntake when all questions are answered

  async function loadAllClients() {
    if (shouldSkipSupabase(readGuestSession()) || !actor) {
      setAllClients([]);
      return;
    }

    try {
      const data = await loadScopedClients(
        supabase,
        actor,
        "*",
        {},
        { includeArchived: canManageArchive },
      );
      setAllClients(data);
    } catch (error) {
      console.error("CLIENT LOAD ERROR:", error);
    }
  }

  async function handleArchiveClient(clientId) {
    if (blockDemoSave()) {
      return;
    }

    if (
      !window.confirm(
        "Archive this client? They will be hidden from normal lists and pickers. Child projects become operationally locked (no new sessions, time logs, estimates, or invoice drafts) while history is preserved. Existing invoices may still be paid or corrected.",
      )
    ) {
      return;
    }

    const { error } = await archiveClient(supabase, clientId, actor);

    if (error) {
      console.error("[ArchiveClient] Failed:", error);
      alert("Unable to archive client.");
      return;
    }

    notifyClientArchived(clientId);

    if (selectedClientId === clientId) {
      setView("hub");
    }

    await loadAllClients();
  }

  async function handleRestoreClient(clientId) {
    if (blockDemoSave()) {
      return;
    }

    if (!window.confirm("Restore this client to the active list?")) {
      return;
    }

    const { error } = await restoreClient(supabase, clientId, actor);

    if (error) {
      console.error("[RestoreClient] Failed:", error);
      alert("Unable to restore client.");
      return;
    }

    notifyClientRestored(clientId);

    await loadAllClients();
    await refreshDeleteEligibility(clientId);
  }

  async function refreshDeleteEligibility(clientId) {
    if (!canManageDelete || !clientId || !actor) {
      setDeleteEligibility(null);
      return;
    }

    const { data, error } = await getClientDeleteEligibility(
      supabase,
      clientId,
      actor,
    );

    if (error) {
      console.error("[ClientDeleteEligibility] Failed:", error);
      setDeleteEligibility(null);
      return;
    }

    setDeleteEligibility(data);
  }

  function beginDeleteConfirm() {
    setDeleteConfirmOpen(true);
    setDeleteConfirmName("");
  }

  function cancelDeleteConfirm() {
    setDeleteConfirmOpen(false);
    setDeleteConfirmName("");
  }

  function closeBlastRadius() {
    setShowBlastRadius(false);
    setBlastRadiusPayload(null);
    setBlastRadiusError("");
  }

  /**
   * Lifecycle entry: always loads the authoritative planner.
   * Unused clients keep the existing permanent-delete confirm.
   * Used clients open the read-only Blast Radius UI.
   */
  async function handlePermanentDeleteEntry() {
    if (!selectedClientId || !actor || blastRadiusBusy) return;
    if (!canInspectBlastRadius && !canManageDelete) return;

    setBlastRadiusBusy(true);
    setBlastRadiusError("");

    const { data, error } = await getClientBlastRadius(
      supabase,
      selectedClientId,
      actor,
    );

    setBlastRadiusBusy(false);

    if (error) {
      console.error("[ClientBlastRadius] Failed:", error);
      setBlastRadiusError(
        error?.message || "Unable to analyze client blast radius.",
      );
      return;
    }

    if (!isSupportedBlastRadiusSchema(data)) {
      setBlastRadiusPayload(data || { schema_version: null });
      setShowBlastRadius(true);
      return;
    }

    if (data?.related?.permanent_delete_eligible && canManageDelete) {
      // Preserve existing unused-client Permanent Delete pathway.
      beginDeleteConfirm();
      return;
    }

    setBlastRadiusPayload(data);
    setShowBlastRadius(true);
  }

  async function handleDeleteClientPermanently(clientId) {
    if (blockDemoSave()) {
      return;
    }

    const client = allClients.find((row) => row.client_id === clientId);
    const expectedName = String(client?.client_name || "").trim();
    if (!expectedName || deleteConfirmName.trim() !== expectedName) {
      alert("Type the exact client name to confirm permanent deletion.");
      return;
    }

    setDeleteBusy(true);
    const { error } = await deleteClientPermanently(supabase, clientId, actor);
    setDeleteBusy(false);

    if (error) {
      console.error("[DeleteClient] Failed:", error);
      alert(
        error?.message?.includes("dependencies")
          ? "This client cannot be deleted because related records exist. Archive it instead."
          : "Unable to permanently delete client.",
      );
      await refreshDeleteEligibility(clientId);
      cancelDeleteConfirm();
      return;
    }

    cancelDeleteConfirm();
    setDeleteEligibility(null);
    setSelectedClientId("");
    setMode("create");
    setView("hub");
    await loadAllClients();
  }

  useEffect(() => {
    if (mode !== "edit" || !selectedClientId || !canManageDelete) {
      setDeleteEligibility(null);
      cancelDeleteConfirm();
      return;
    }

    refreshDeleteEligibility(selectedClientId);
  }, [mode, selectedClientId, canManageDelete, actor]);

  const search = searchTerm.toLowerCase().trim();
  const hubClients = getHubClientsForStatusFilter(allClients, hubStatusFilter);
  const filteredClients = hubClients.filter((client) =>
    clientMatchesSearch(client, search),
  );
  const selectedClient = allClients.find(
    (client) => client.client_id === selectedClientId,
  );
  const selectedClientArchived = isClientArchived(selectedClient);
  const selectedClientDeletable =
    canManageDelete && isClientPermanentlyDeletable(deleteEligibility);
  const showPermanentDeleteEntry =
    mode === "edit" &&
    Boolean(selectedClientId) &&
    (canInspectBlastRadius || canManageDelete);
  const deleteNameMatches =
    Boolean(selectedClient?.client_name) &&
    deleteConfirmName.trim() === String(selectedClient.client_name).trim();

  function resetFormFields() {
    setClientName("");
    setContactName("");
    setPhone("");
    setSecondaryPhone("");
    setEmail("");
    setAddress("");
    setCity("");
    setProvince("");
    setPostalCode("");
    setCountry("");
    setClientStatus("active");
    setNotes("");
  }

  function openCreateForm() {
    setMode("create");
    setSelectedClientId("");
    setDeleteEligibility(null);
    setDeleteConfirmOpen(false);
    setDeleteConfirmName("");

    if (!localStorage.getItem(CLIENT_DRAFT_KEY)) {
      resetFormFields();
    }

    setView("form");
  }

  function openClientDetail(clientId) {
    setSelectedClientId(clientId);
    setMode("edit");
    setDeleteConfirmOpen(false);
    setDeleteConfirmName("");
    setView("detail");
  }

  function openEditForm(clientId = selectedClientId) {
    if (!clientId) return;
    setMode("edit");
    setSelectedClientId(clientId);
    setDeleteConfirmOpen(false);
    setDeleteConfirmName("");
    setView("form");
  }

  function handleDetailBack() {
    setView("hub");
  }

  function handleSwipeBack() {
    if (showBlastRadius) {
      closeBlastRadius();
      return;
    }

    if (view === "form") {
      if (embedFromEstimate) {
        onCancel?.();
        return;
      }

      // Return to Client Intelligence when editing an existing client.
      if (mode === "edit" && selectedClientId) {
        setView("detail");
        return;
      }

      setView("hub");
      return;
    }

    if (view === "detail") {
      setView("hub");
      return;
    }

    onBack?.();
  }

  useHardwareBackHandler(() => {
    handleSwipeBack();
    return true;
  });

  const carouselEnabled =
    Boolean(onCarouselNavigate) && view === "hub" && !embedFromEstimate;
  const { ref: carouselRef, style: carouselTouchStyle } =
    useDashboardCarouselSwipe({
      onNavigate: onCarouselNavigate,
      onOpenNavigator,
      enabled: carouselEnabled,
    });

  function handleCancelForm() {
    if (embedFromEstimate) {
      onCancel?.();
      return;
    }

    if (mode === "edit" && selectedClientId) {
      setView("detail");
      return;
    }

    setView("hub");
  }

  const handleSubmit = async () => {
    if (blockDemoSave()) {
      return;
    }

    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (mode === "edit") {
      try {
        const { error } = await updateScopedClient(supabase, actor, selectedClientId, {
            client_name: clientName,
            contact_name: contactName,

            phone: phone,
            secondary_phone: secondaryPhone,

            email: email.trim() === "" ? null : email,

            address: address,
            city: city,
            province: province,
            postal_code: postalCode,
            country: country,

            client_status: clientStatus,

            notes: notes,
          });

        if (error) throw error;

        setSubmitted(true);
        return;
      } catch (error) {
        console.error("[UpdateClient] Failed:", error);

        alert("Unable to update client.");

        return;
      }
    }

    const payload = {
      raw_input: "create client",

      user_id: user?.id || "",
      supabase_user_id: user?.id || "",

      client_name: clientName || "",
      contact_name: contactName || "",

      phone: phone || "",
      secondary_phone: secondaryPhone || "",

      email: email || "",

      address: address || "",
      city: city || "",
      province: province || "",
      postal_code: postalCode || "",
      country: country || "",

      client_status: clientStatus || "",

      notes: notes || "",

      source: "create_client_screen",
      timestamp: new Date().toISOString(),
    };

    // ── n8n webhook integration point ────────────────────────
    // TODO: replace console.log with:
    //
    // fetch("https://YOUR-N8N-INSTANCE/webhook/create-client", {
    //   method: "POST",
    //   headers: { "Content-Type": "application/json" },
    //   body: JSON.stringify(payload),
    // });
    //
    try {
      const { data, error } = await insertScopedClient(supabase, actor, {
            client_name: clientName,

            contact_name: contactName,

            phone: phone,
            secondary_phone: secondaryPhone,

            email: email.trim() === "" ? null : email,

            address: address,
            city: city,
            province: province,
            postal_code: postalCode,
            country: country,

            client_status: clientStatus,

            notes: notes,
            created_by: user.id,
          });

      if (error) throw error;

      localStorage.removeItem(CLIENT_DRAFT_KEY);

      if (embedFromEstimate && onClientCreated) {
        onClientCreated(data);
        return;
      }

      setSubmitted(true);
    } catch (error) {
      console.error("[CreateClient] Failed:", error);

      alert("Unable to create client.");
    }
  };

  // ── SUCCESS STATE ─────────────────────────────────────────
  if (submitted) {
    return (
      <div className="screen nx-screen ci-success-screen">
        <div className="ci-success-content">
          <div className="ci-success-icon">
            <svg
              viewBox="0 0 52 52"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="checkmark-circle nx-checkmark-circle"
                cx="26"
                cy="26"
                r="24"
              />
              <path
                className="checkmark-check  nx-checkmark-check"
                d="M14.1 27.2l7.1 7.2 16.7-16.8"
              />
            </svg>
          </div>
          <h1 className="ci-success-title">
            {mode === "edit" ? "Client Updated" : "Client Created"}
          </h1>
          <p className="ci-success-sub">
            {clientName || "Client"} has been{" "}
            {mode === "edit" ? "updated" : "added"}.
          </p>
          <button
            className="nx-btn nx-btn-gold"
            style={{ marginTop: "12px" }}
            onClick={() => {
              setSubmitted(false);
              loadAllClients();
              setView("hub");
            }}
          >
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  const swipeHandlers = {
    onTouchStart: (e) => {
      if (e.touches.length !== 1) return;

      touchStartX.current = e.touches[0].clientX;
    },
    onTouchEnd: (e) => {
      if (touchStartX.current === null) return;

      if (e.changedTouches.length !== 1) {
        touchStartX.current = null;
        return;
      }

      const deltaX = e.changedTouches[0].clientX - touchStartX.current;

      if (Math.abs(deltaX) > 80) {
        handleSwipeBack();
      }

      touchStartX.current = null;
    },
  };

  if (showBlastRadius && blastRadiusPayload) {
    return (
      <div
        className="screen nx-screen client-blast-radius-host"
        data-testid="client-blast-radius-host"
      >
        <ClientBlastRadiusScreen
          payload={blastRadiusPayload}
          clientName={selectedClient?.client_name || clientName}
          onBackToClient={closeBlastRadius}
        />
      </div>
    );
  }

  if (view === "detail" && selectedClient) {
    return (
      <div {...swipeHandlers}>
        <ClientIntelligenceScreen
          client={selectedClient}
          onBack={handleDetailBack}
          onEditClient={() => openEditForm(selectedClient.client_id)}
          onOpenProject={onOpenProjectDetail}
          onOpenInvoice={onOpenInvoice}
          onOpenProjectsForClient={onOpenProjectsForClient}
          onOpenTimeLogsForClient={onOpenTimeLogsForClient}
        />
      </div>
    );
  }

  if (view === "hub" && isDesktopViewport && !embedFromEstimate) {
    return (
      <ClientsDesktopHub
        allClients={allClients}
        canManageArchive={canManageArchive}
        onBack={onBack}
        onNew={openCreateForm}
        onOpenClient={openClientDetail}
        onArchive={handleArchiveClient}
        onRestore={handleRestoreClient}
      />
    );
  }

  if (view === "hub") {
    return (
      <div
        ref={carouselRef}
        style={carouselTouchStyle}
        {...(carouselEnabled ? {} : swipeHandlers)}
      >
        <CrewPilotModuleShell
          title="Clients"
          subtitle="Client Relationship Management"
          onBack={onBack}
          layout={PAGE_LAYOUT.COMMAND}
        >
          <div
            style={{
              fontSize: "1.25rem",
              fontWeight: 600,
              marginBottom: "16px",
              color: "var(--nx-text)",
            }}
          >
            Clients ({filteredClients.length}
            {canManageArchive && hubStatusFilter === "archived" ? " archived" : ""})
          </div>

          <div className="ch-toolbar">
          {canManageArchive ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                marginBottom: "16px",
              }}
            >
              {CLIENT_HUB_STATUS_FILTERS.map((filter) => {
                const isSelected = hubStatusFilter === filter.value;
                return (
                  <button
                    key={filter.value}
                    type="button"
                    onClick={() => setHubStatusFilter(filter.value)}
                    style={{
                      ...STATUS_PILL_BTN_BASE_STYLE,
                      ...(isSelected ? HUB_STATUS_PILL_ACTIVE_GOLD_STYLE : {}),
                    }}
                  >
                    {filter.label}
                  </button>
                );
              })}
            </div>
          ) : null}

          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search clients..."
            style={SEARCH_INPUT_STYLE}
          />

          {hubStatusFilter !== "archived" ? (
            <div className="hub-primary-slot" style={{ marginTop: "16px" }}>
              <HubPrimaryAction
                onClick={openCreateForm}
                label="New Client"
                ariaLabel="+ New Client"
              />
            </div>
          ) : null}
          </div>

          <div className="ch-grid" style={{ marginTop: "20px" }}>
            {filteredClients.length === 0 ? (
              <p
                style={{
                  textAlign: "center",
                  marginTop: "24px",
                  color: "var(--nx-text-muted, #5b6472)",
                }}
              >
                {search
                  ? "No clients match your search."
                  : hubStatusFilter === "archived"
                    ? "No archived clients."
                    : "No clients found."}
              </p>
            ) : (
              filteredClients.map((client) => (
                <ClientHubCard
                  key={client.client_id}
                  client={client}
                  onSelect={openClientDetail}
                  onArchive={handleArchiveClient}
                  onRestore={handleRestoreClient}
                  canManageArchive={canManageArchive}
                />
              ))
            )}
          </div>
        </CrewPilotModuleShell>
      </div>
    );
  }

  const clientLifecyclePanel =
    mode === "edit" &&
    (canManageArchive || canManageDelete || canInspectBlastRadius) &&
    selectedClientId ? (
      <div
        style={{
          marginTop: "8px",
          paddingTop: "16px",
          borderTop: "1px solid rgba(201,168,76,0.18)",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <div className="kpi-section-label">Lifecycle</div>
        {canManageArchive ? (
          selectedClientArchived ? (
            <button
              type="button"
              className="nx-btn"
              onClick={() => handleRestoreClient(selectedClientId)}
            >
              Restore Client
            </button>
          ) : (
            <button
              type="button"
              className="nx-btn cp-btn-archive"
              onClick={() => handleArchiveClient(selectedClientId)}
            >
              Archive Client
            </button>
          )
        ) : null}

        {selectedClientDeletable && deleteConfirmOpen ? (
          <div style={DELETE_CLIENT_CONFIRM_PANEL_STYLE}>
            <div style={{ fontSize: "14px", lineHeight: 1.45, color: "#f0c4bf" }}>
              Permanently delete this client? This cannot be undone. Only
              unused clients with no projects, estimates, invoices, time
              entries, sessions, or workspace records can be deleted.
            </div>
            <label style={{ fontSize: "13px", opacity: 0.85 }}>
              Type <strong>{selectedClient?.client_name}</strong> to confirm
            </label>
            <input
              type="text"
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              placeholder="Type client name to confirm"
              aria-label="Type client name to confirm permanent delete"
              autoComplete="off"
              style={{
                width: "100%",
                padding: "12px 14px",
                borderRadius: "10px",
                border: "1px solid rgba(201,55,44,0.4)",
                background: "var(--nx-surface2, #f8f9fb)",
                color: "var(--nx-text, #1a2233)",
                fontSize: "15px",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                type="button"
                className="nx-btn"
                onClick={cancelDeleteConfirm}
                disabled={deleteBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDeleteClientPermanently(selectedClientId)}
                disabled={!deleteNameMatches || deleteBusy}
                style={{
                  ...DELETE_CLIENT_BTN_STYLE,
                  opacity: !deleteNameMatches || deleteBusy ? 0.45 : 1,
                  cursor:
                    !deleteNameMatches || deleteBusy ? "not-allowed" : "pointer",
                  padding: "10px 14px",
                }}
              >
                {deleteBusy ? "Deleting…" : "Permanently Delete"}
              </button>
            </div>
          </div>
        ) : null}

        {showPermanentDeleteEntry && !deleteConfirmOpen ? (
          <>
            <button
              type="button"
              className="cbr-entry-link"
              onClick={handlePermanentDeleteEntry}
              disabled={blastRadiusBusy}
              data-testid="client-permanent-delete-entry"
            >
              <BiohazardMark size={13} className="cbr-entry-link__mark" />
              {blastRadiusBusy
                ? "Analyzing blast radius…"
                : "Permanently delete this client?"}
            </button>
            <div className="cbr-entry-hint">
              Opens a read-only impact analysis when this client has
              dependencies. Unused clients still use the existing permanent
              delete confirmation.
            </div>
            {blastRadiusError ? (
              <div
                style={{
                  fontSize: "13px",
                  color: "#f0c4bf",
                  marginTop: "4px",
                }}
                data-testid="client-blast-radius-error"
              >
                {blastRadiusError}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    ) : null;

  if (!isDesktopViewport) {
    const mobileSubtitle = embedFromEstimate
      ? "Add a client and return to your estimate"
      : mode === "edit"
        ? "Update client details"
        : "Add a new client";

    return (
      <div {...swipeHandlers}>
        <ClientMobileForm
          title={mode === "edit" ? "Edit Client" : "Create Client"}
          subtitle={mobileSubtitle}
          onCancel={handleCancelForm}
          onSubmit={handleSubmit}
          saveDisabled={selectedClientArchived}
          showArchived={mode === "edit" && selectedClientArchived}
          clientName={clientName}
          setClientName={setClientName}
          contactName={contactName}
          setContactName={setContactName}
          phone={phone}
          setPhone={setPhone}
          secondaryPhone={secondaryPhone}
          setSecondaryPhone={setSecondaryPhone}
          email={email}
          setEmail={setEmail}
          address={address}
          setAddress={setAddress}
          city={city}
          setCity={setCity}
          province={province}
          setProvince={setProvince}
          postalCode={postalCode}
          setPostalCode={setPostalCode}
          country={country}
          setCountry={setCountry}
          clientStatus={clientStatus}
          setClientStatus={setClientStatus}
          statusDisabled={selectedClientArchived}
          notes={notes}
          setNotes={setNotes}
          extras={
            <>
              {mode === "edit" && selectedClientId ? (
                <ClientWorkspaceSection
                  clientId={selectedClientId}
                  clientName={clientName}
                  actor={actor}
                />
              ) : null}
              {clientLifecyclePanel}
            </>
          }
        />
      </div>
    );
  }

  // ── FORM STATE (desktop) ──────────────────────────────────
  return (
  <div {...swipeHandlers}>
    <CrewPilotFormShell
      title={mode === "edit" ? "Edit Client" : "Create Client"}
      subtitle={
        embedFromEstimate
          ? "Add a client and return to your estimate"
          : "Client Relationship Management"
      }
      onBack={handleCancelForm}
      backLabel={
        embedFromEstimate
          ? "Estimate"
          : mode === "edit" && selectedClientId
            ? clientName || "Client"
            : "Clients"
      }
    >
      <div
        className="cp-form-compact"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {mode === "edit" && selectedClientArchived ? (
          <div style={ARCHIVED_BADGE_STYLE}>Archived Client</div>
        ) : null}

        {/* CLIENT INFORMATION */}
        <section className="cp-form-card cp-form-card--client">
        <h3 className="cp-section-title kpi-section-label">CLIENT INFORMATION</h3>

        <CPInput
          label="Client Name"
          value={clientName}
          onChange={setClientName}
          placeholder="Enter client name"
          required
        />

        <CPInput
          label="Contact Name"
          value={contactName}
          onChange={setContactName}
          placeholder="Enter contact name"
        />
        </section>

        {/* CONTACT INFORMATION */}
        <section className="cp-form-card cp-form-card--contact">
        <h3 className="cp-section-title kpi-section-label">CONTACT INFORMATION</h3>

        <div className="cp-row-grid-2">
          <CPInput
            label="Phone"
            value={phone}
            onChange={setPhone}
            placeholder="Enter phone number"
            required
          />

          <CPInput
            label="Secondary Phone"
            value={secondaryPhone}
            onChange={setSecondaryPhone}
            placeholder="Enter secondary phone"
          />
        </div>

        <CPInput
          label="Email"
          value={email}
          onChange={setEmail}
          placeholder="Enter email address"
          type="email"
          required
        />
        </section>

        {/* ADDRESS */}
        <section className="cp-form-card cp-form-card--address">
        <h3 className="cp-section-title kpi-section-label">ADDRESS</h3>

        <CPInput
          label="Address"
          value={address}
          onChange={setAddress}
          placeholder="Enter street address"
        />

        <div className="cp-row-grid-2">
          <CPInput
            label="City"
            value={city}
            onChange={setCity}
            placeholder="Enter city"
          />

          <CPInput
            label="Province"
            value={province}
            onChange={setProvince}
            placeholder="Enter province"
          />
        </div>

        <div className="cp-row-grid-2">
          <CPInput
            label="Postal Code"
            value={postalCode}
            onChange={setPostalCode}
            placeholder="Enter postal code"
          />

          <CPInput
            label="Country"
            value={country}
            onChange={setCountry}
            placeholder="Enter country"
          />
        </div>
        </section>

        <section className="cp-form-card cp-form-card--details">
        <CPDropdown
          label="Client Status"
          value={clientStatus === "archived" ? "active" : clientStatus}
          onChange={setClientStatus}
          placeholder="Select status"
          options={[
            { value: "active", label: "Active" },
            { value: "inactive", label: "Inactive" },
            { value: "prospect", label: "Prospect" },
          ]}
          disabled={selectedClientArchived}
        />

        <CPTextarea
          label="Notes"
          value={notes}
          onChange={setNotes}
          placeholder="Enter client notes"
        />
        </section>

        {mode === "edit" && selectedClientId ? (
          <ClientWorkspaceSection
            clientId={selectedClientId}
            clientName={clientName}
            actor={actor}
          />
        ) : null}

        <div
          className="cp-form-actions"
          style={{
            display: "flex",
            gap: "12px",
          }}
        >
          <button className="nx-btn" onClick={handleCancelForm}>
            Cancel
          </button>

          <button
            className="nx-btn nx-btn-gold"
            onClick={handleSubmit}
            disabled={selectedClientArchived}
          >
            Save Client
          </button>
        </div>

        {clientLifecyclePanel}
      </div>
    </CrewPilotFormShell>
    </div>
  );
}
